/* eslint-disable camelcase */
import {
  Button,
  NativeSelect,
  Snackbar,
  Typography,
} from '@equinor/eds-core-react';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadioPicker } from './components/RadioPicker';
import { TableRow } from './components/TableRow';
import {
  NameInput,
  ScenarioBuilderWrapper,
  TextInput,
  Wrapper,
} from './ScenarioBuilder.styled';
import TemplateDetailsDialog from '../../components/TemplateDetailsDialog';
import * as StyledCard from '../../styles/Card/Card.styled';
import { useFetchScenarioTemplates } from '../../hooks/useFetchScenarioTemplates';
import { useScenarioStore } from './stores/ScenarioStore';
import ResetParamsDialog from './components/ResetParamsDialog';
import DuplicateModelDialog from './components/DuplicateModelDialog';
import {
  postApiV1Scenarios,
  postApiV1ScenariosByScenarioIdStartOrchestration,
} from '../../api/generated';
import type { CreateScenarioCommandResponse } from '../../api/generated/types.gen';
import ParametersTable from '../../components/ParametersTable';
import { useErrorStore } from '../../stores/ErrorStore';
import { useIsReader } from '../../hooks/useIsReader';

const isTemplateUnderTechnicalReview = (templateName: string) =>
  ['blackhawk', 'cliffhouse'].includes(
    templateName.toLowerCase().replace(/\s+/g, ''),
  );

export const ScenarioBuilder = () => {
  const navigate = useNavigate();
  const isReader = useIsReader();
  const templatesData = useFetchScenarioTemplates();
  const {
    templates,
    setTemplates,
    setCurrentTemplate,
    currentTemplate,
    parameters,
    name,
    setName,
    description,
    setDescription,
    validationErrors,
    reset: resetScenarioStore,
  } = useScenarioStore();

  const { addError } = useErrorStore();
  const restrictTemplates =
    process.env.REACT_APP_ENVIRONMENT?.toUpperCase() === 'PROD';

  useEffect(() => {
    return () => resetScenarioStore();
  }, [resetScenarioStore]);

  const [selectedName, setSelectedName] = useState<string>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenCreate, setIsOpenCreate] = useState<boolean>(false);
  const [isOpenReset, setIsOpenReset] = useState<boolean>(false);
  const [templateError, setTemplateError] = useState<string>();
  const [nameError, setNameError] = useState<string>();
  const [descriptionError, setDescriptionError] = useState<string>();
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [startedScenarioId, setStartedScenarioId] = useState<string>();
  const [existingAnalogueModelId, setExistingAnalogueModelId] =
    useState<string>();

  useEffect(() => {
    if (templatesData?.data?.data?.data) {
      setTemplates(templatesData.data.data.data);
      if (selectedName === undefined) {
        const initialTemplate = templatesData.data.data.data.find(
          (template) =>
            !restrictTemplates ||
            !isTemplateUnderTechnicalReview(template.name),
        );
        setSelectedName(
          initialTemplate?.name ?? templatesData.data.data.data[0]?.name,
        );
      }
    }
  }, [templatesData, setTemplates, selectedName, restrictTemplates]);

  const selectOnChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const name = templates.find((t) => t.name === event.target.value)?.name;
    if (name) {
      setSelectedName(name);
      setTemplateError(undefined);
    }
  };

  const updateTemplate = () => {
    const template = templates.find((t) => t.name === selectedName);
    if (template) {
      setCurrentTemplate(template);
    }
  };

  const buttonOnConfirm = () => {
    let hasError = false;

    if (!selectedName) {
      setTemplateError('You must select a template');
      hasError = true;
    } else {
      setTemplateError(undefined);
    }

    if (!name) {
      setNameError('You must add a name');
      hasError = true;
    } else {
      setNameError(undefined);
    }

    if (!description) {
      setDescriptionError('You must add a description');
      hasError = true;
    } else {
      setDescriptionError(undefined);
    }

    if (hasError) return;

    if (currentTemplate) {
      setIsOpenReset(true);
    } else {
      updateTemplate();
    }
  };

  const onSimulationStart = async () => {
    if (!currentTemplate || !parameters) return;

    try {
      // Create scenario with data from ScenarioStore
      const createResponse = await postApiV1Scenarios({
        body: {
          name: name,
          description: description || '',
          json_scenario_data: JSON.stringify(parameters),
          scenario_template_id: currentTemplate.scenarioTemplateId,
        },
      });

      // Check for 409 Conflict (duplicate) in error response
      if (createResponse.error && createResponse.response?.status === 409) {
        const errorData = createResponse.error as CreateScenarioCommandResponse;
        if (errorData?.data?.analogue_model_id) {
          setExistingAnalogueModelId(errorData.data.analogue_model_id);
        } else {
          setStartedScenarioId(errorData.data.scenario_id);
        }
        setIsOpenCreate(true);
        return;
      }

      if (
        createResponse.data?.success &&
        createResponse.data.data.scenario_id
      ) {
        const scenario_id = createResponse.data.data.scenario_id;
        const orchestrationResponse =
          await postApiV1ScenariosByScenarioIdStartOrchestration({
            path: { scenarioId: scenario_id },
          });
        if (orchestrationResponse.data?.success) {
          setStartedScenarioId(scenario_id);
        }

        setShowSnackbar(true);
      }
    } catch (error: unknown) {
      addError(
        (error instanceof Error && error.message) ||
          'An unknown error occurred while starting the simulation.',
      );
    }
  };

  type variable = {
    name: string;
    validators: {
      max: number;
      min: number;
      allowedValues: number[];
    };
    units: string;
    type: string;
    id: string;
    default: number;
    factor: boolean;
    description: string;
  };

  return (
    <>
      <ScenarioBuilderWrapper>
        <StyledCard.ShadowCard>
          <StyledCard.ShadowCard.Header>
            <StyledCard.ShadowCard.HeaderTitle>
              <Typography variant="h4" as="h3">
                Create template-based models for further parameter estimation.
              </Typography>
            </StyledCard.ShadowCard.HeaderTitle>
          </StyledCard.ShadowCard.Header>
          <StyledCard.Content>
            <div style={{ display: 'flex', alignItems: 'end', gap: '1rem' }}>
              <div>
                <NativeSelect
                  label="Template"
                  id="native-select"
                  onChange={selectOnChange}
                  required
                  style={{ maxWidth: '390px' }}
                  value={selectedName}
                >
                  {templates &&
                    templates.map((t) => (
                      <option
                        key={t.scenarioTemplateId}
                        disabled={
                          restrictTemplates &&
                          isTemplateUnderTechnicalReview(t.name)
                        }
                      >
                        {t.name}
                      </option>
                    ))}
                </NativeSelect>
                {templateError && (
                  <Typography variant="caption" style={{ color: 'red' }}>
                    {templateError}
                  </Typography>
                )}
              </div>
              {currentTemplate &&
                TemplateDetailsDialog({
                  tooltipTitle: currentTemplate.name,
                  description: currentTemplate.description,
                  picture: 'currentTemplate.fields.picture',
                  setIsOpen: setIsOpen,
                  isOpen: isOpen,
                })}
            </div>

            <NameInput
              id={'Name'}
              label="Name"
              required
              value={name}
              onChange={(e: { target: { value: string } }) => {
                setName(e.target.value);
                setNameError(undefined);
              }}
              variant={nameError ? 'error' : undefined}
              helperText={nameError}
            />

            <TextInput
              id={'Description'}
              label="Description"
              required
              rows={8}
              value={description}
              onChange={(e: { target: { value: string } }) => {
                setDescription(e.target.value);
                setDescriptionError(undefined);
              }}
              variant={descriptionError ? 'error' : undefined}
              helperText={descriptionError}
            />

            <Button
              variant="outlined"
              onClick={buttonOnConfirm}
              style={{ maxWidth: '140px' }}
            >
              Load template
            </Button>
          </StyledCard.Content>
        </StyledCard.ShadowCard>
        {currentTemplate && (
          <ResetParamsDialog
            isOpen={isOpenReset}
            setIsOpen={setIsOpenReset}
            updateTemplate={updateTemplate}
          />
        )}

        {currentTemplate && (
          <StyledCard.ShadowCard>
            <StyledCard.ShadowCard.Header>
              <StyledCard.ShadowCard.HeaderTitle>
                <Typography variant="h4" as="h3">
                  Simulation parameters
                </Typography>
              </StyledCard.ShadowCard.HeaderTitle>
            </StyledCard.ShadowCard.Header>
            <StyledCard.Content>
              {(
                JSON.parse(currentTemplate.jsonData)['fields'][
                  'sections'
                ] as Array<{ id: string; name: string; variables: variable[] }>
              ).map((s) => {
                if (s.name !== 'Scenario' && s.name !== 'Sediment composition')
                  return (
                    <Wrapper key={s.id + s.name}>
                      <ParametersTable
                        sectionName={s.name}
                        style={{
                          minWidth: '600px',
                          width: '30vw',
                        }}
                      >
                        {s.variables.map((v) => {
                          const variable = v as variable;
                          return (
                            <TableRow
                              key={s.id + v.id}
                              variable={variable}
                            ></TableRow>
                          );
                        })}
                      </ParametersTable>
                    </Wrapper>
                  );
                else if (s.name === 'Sediment composition') {
                  const variable = s.variables[0] as unknown as {
                    id: string;
                    options: {
                      text: string;
                      value: string;
                    }[];
                  };
                  return (
                    <div key={'radio' + s.name}>
                      <Typography
                        variant="body_short_bold"
                        style={{
                          marginBottom: '10px',
                        }}
                      >
                        {s.name}
                      </Typography>
                      <RadioPicker optionsList={variable.options}></RadioPicker>
                    </div>
                  );
                }
                return null;
              })}
              {Object.keys(validationErrors).length > 0 && (
                <Typography
                  group="input"
                  variant="helper"
                  style={{ color: '#B30D2F' }}
                >
                  Fix validation errors before starting the simulation.
                </Typography>
              )}
              <Button
                onClick={onSimulationStart}
                style={{ width: '140px' }}
                disabled={isReader || Object.keys(validationErrors).length > 0}
                title={
                  isReader
                    ? 'Readers cannot start simulations'
                    : Object.keys(validationErrors).length > 0
                    ? 'Fix validation errors before starting'
                    : undefined
                }
              >
                Start simulation
              </Button>
            </StyledCard.Content>
          </StyledCard.ShadowCard>
        )}
        {currentTemplate && (
          <DuplicateModelDialog
            isOpen={isOpenCreate}
            setIsOpen={setIsOpenCreate}
            duplicateId={existingAnalogueModelId ?? startedScenarioId}
            isAnalogueModel={!!existingAnalogueModelId}
          />
        )}
      </ScenarioBuilderWrapper>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={5000}
        onClose={() => setShowSnackbar(false)}
      >
        Simulation scheduled for processing
        <Snackbar.Action>
          <Button
            variant="ghost"
            onClick={() => {
              setShowSnackbar(false);
              if (startedScenarioId) {
                navigate(`/simulations/${startedScenarioId}`);
              }
            }}
          >
            GO TO SIMULATION
          </Button>
        </Snackbar.Action>
      </Snackbar>
    </>
  );
};
